import React from 'react';

import type {StepStatusKind} from './useStepStatus';

interface SetupStepSectionProps {
  stepNumber: number;
  title: string;
  status: StepStatusKind;
  summary: string;
  errorCount?: number;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const STATUS_CONFIG: Record<
  StepStatusKind,
  {
    circleBg: string;
    circleText: string;
    titleText: string;
    summaryText: string;
    icon: string | null; // null = show step number
  }
> = {
  not_started: {
    circleBg: 'bg-gray-200',
    circleText: 'text-gray-500',
    titleText: 'text-gray-500',
    summaryText: 'text-gray-400',
    icon: null,
  },
  in_progress: {
    circleBg: 'bg-blue-100',
    circleText: 'text-blue-700',
    titleText: 'text-blue-700',
    summaryText: 'text-blue-500',
    icon: null,
  },
  complete: {
    circleBg: 'bg-green-100',
    circleText: 'text-green-700',
    titleText: 'text-gray-900',
    summaryText: 'text-green-600',
    icon: '✓',
  },
  warning: {
    circleBg: 'bg-amber-100',
    circleText: 'text-amber-700',
    titleText: 'text-gray-900',
    summaryText: 'text-amber-600',
    icon: '!',
  },
  error: {
    circleBg: 'bg-red-100',
    circleText: 'text-red-700',
    titleText: 'text-gray-900',
    summaryText: 'text-red-600',
    icon: '!',
  },
};

export default function SetupStepSection({
  stepNumber,
  title,
  status,
  summary,
  errorCount,
  isExpanded,
  onToggle,
  children,
}: SetupStepSectionProps) {
  const config = STATUS_CONFIG[status];
  const contentId = `step-${stepNumber}-content`;

  return (
    <div className="rounded-lg border border-gray-200">
      {/* Header — always visible */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        {/* Step circle */}
        <div
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold ${config.circleBg} ${config.circleText}`}
        >
          {config.icon ?? stepNumber}
        </div>

        {/* Title + summary */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${config.titleText}`}>{title}</span>
            {errorCount != null && errorCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                {errorCount} {errorCount === 1 ? 'issue' : 'issues'}
              </span>
            )}
          </div>
          {!isExpanded && <p className={`text-xs ${config.summaryText} truncate`}>{summary}</p>}
        </div>

        {/* Expand/collapse chevron */}
        <svg
          className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div id={contentId} className="px-4 pb-4 pt-1">
          {children}
        </div>
      )}
    </div>
  );
}

/** Inline prerequisite warning banner to place at top of section content. */
export function PrerequisiteWarning({
  message,
  onGoToStep,
  stepLabel,
}: {
  message: string;
  onGoToStep?: () => void;
  stepLabel?: string;
}) {
  return (
    <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 p-3">
      <p className="text-sm text-amber-800">
        {message}
        {onGoToStep && stepLabel && (
          <button
            type="button"
            className="underline ml-1 font-medium hover:text-amber-900"
            onClick={onGoToStep}
          >
            Go to {stepLabel}
          </button>
        )}
      </p>
    </div>
  );
}

/** Inline validation error list to place at top of section content. */
export function SectionErrors({errors}: {errors: string[]}) {
  if (errors.length === 0) return null;
  return (
    <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3">
      <p className="text-sm font-medium text-red-800">Validation errors</p>
      <ul className="mt-1 list-disc list-inside text-sm text-red-700">
        {errors.map((msg, i) => (
          <li key={i}>{msg}</li>
        ))}
      </ul>
    </div>
  );
}
